import React, { useEffect, useState } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form } from 'antd'
import { Divider, Stack, Typography } from '@mui/material'
import { Button, Container, Col, Row } from 'react-bootstrap'
import { saveAs } from 'file-saver'
import { IoMdDownload, IoMdOpen } from "react-icons/io"

import useFetchData from 'Shared/Hooks/useFetchData'
import { CheckList, Dropdown, TextInput, FormContainer } from 'Shared/Components/FormComponents'
import { apiUrl } from 'Shared/utils'
import BackLinkButton from 'Shared/Components/BackLinkButton'
import { AttachmentField } from 'Shared/Components/FormComponents'
import { IconButtonComponent } from 'Shared/Components/Icon'

const AuthItem = ({ form, authorizationPerson, i }) => {
  const option = Form.useWatch(`${i["record_id"]}-0`, { form, preserve: true })

  return (
    <div>
      <div>{i["authorization"]}</div>

      <CheckList field_id={`${i["record_id"]}-0`} no_margin props={{"options": [{"value": "1", "label": "Select from Worker List"}, {"value": "2", "label": "Other"}]}} required={true}/>

      {option === "1" &&
        <Dropdown field_id={`${i["record_id"]}-1`} no_margin props={{ "multiple": false, "options": authorizationPerson }} required/>
      }

      {option === "2" &&
        <TextInput field_id={`${i["record_id"]}-2`} no_margin required/>
      }
    </div>
  )
}

const UploadForm = ({ title, reload, authorizationItems, authorizationPerson, formDetail, step }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { send } = useFetchData()
  const { form_id } = useParams()
  const { setPageLoading } = useOutletContext()

  const onUploadFinish = async (values) => {
    setPageLoading(true)

    const formData = new FormData()

    for (const file of values["attachment"]["fileList"]) {
      formData.append('files', file.originFileObj)
    }

    const step = (formDetail["status"] === 0 || formDetail["status"] === -100) ? 0 : 1

    if (step == 0) {
      formData.append('authorization', JSON.stringify(authorizationItems.map((i) => ({
        "authorization_item": i["record_id"],
        "worker_id": values[`${i["record_id"]}-1`] ? values[`${i["record_id"]}-1`] : "",
        "name": values[`${i["record_id"]}-2`] ? values[`${i["record_id"]}-2`] : ""
      }))))
    }
    else {
      formData.append('authorization', JSON.stringify([]))
    }

    const body = await send({
      method: "POST",
      url: `${apiUrl}/permit_to_work/upload`,
      contentType: "formdata",
      params: {
        "form_id": form_id
      },
      body: formData
    })

    if (body != null) {
      await reload()
    }

    setPageLoading(false)
  }

  return (
    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      <Typography variant="h6"><b>{title}</b></Typography>

      <FormContainer form={form} onFinish={onUploadFinish} initialValues={{}}>
        <AttachmentField field_id="attachment" field_name={t("permitToWork_upload")} required props={{"accept": ".jpeg, .png, .pdf"}}/>
      
        {(step === 0 && authorizationItems != null) && <>
          {(authorizationItems.length > 0) && <>
            <div>
              <div style={{ marginTop: "15px", marginBottom: "15px"}}>
                <Typography variant="body1"><b>Authorization</b></Typography>
              </div>

              <Stack direction="column" spacing={3} style={{ marginTop: "15px", marginBottom: "15px" }}>
                {authorizationItems.map((i) =>
                  <div>
                    <AuthItem form={form} authorizationPerson={authorizationPerson} i={i}/>
                  </div>
                )}
              </Stack>
            </div>
          </>}
        </>}
      </FormContainer>
    </div>
  )
}

const VoidDisplay = ({ voidInfo }) => {
  const { t } = useTranslation()

  return (
    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      <div>
          <Typography variant="h6"><b>{t("permitToWork_permitVoidance")}</b></Typography>
      </div>

      <div style={{ height: "20px" }}/>

      <div>
        <div>{t("permitToWork_reason")}: {voidInfo["reason"]}</div>
      </div>
    </div>
  )
}

const VoidForm = ({ reload }) => {
  const { setPageLoading } = useOutletContext()
  const { t } = useTranslation()
  const { send } = useFetchData()

  const [form] = Form.useForm()
  const { form_id } = useParams()

  const onUploadFinish = async (values) => {
    setPageLoading(true)

    const body = await send({
      method: "POST",
      url: `${apiUrl}/permit_to_work/void`,
      contentType: "json",
      params: {
        "form_id": form_id
      },
      body: {
        "reason": values["reason"]
      }
    })

    if (body != null) {
      await reload()
    }

    setPageLoading(false)
  }

  return (
    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      <Typography variant="h6"><b>{t("permitToWork_permitVoidance")}</b></Typography>

      <FormContainer form={form} onFinish={onUploadFinish} initialValues={{}}>
        <TextInput field_id="reason" field_name={t("permitToWork_reason")} required/>
      </FormContainer>
    </div>
  )
}

const ResultDisplay = ({ title, uploadedAttachment, authorizationRecord, step }) => {
  const { setPageLoading } = useOutletContext()
  const { role } = useOutletContext()
  const { t } = useTranslation()
  const { form_id } = useParams()
  const { send } = useFetchData()

  const downloadSingleFile = async (filename, save_file) => {
    setPageLoading(true)

    const blob = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/attachments/download_single`,
      returnType: "blob",
      params: {
        "form_id": form_id,
        "filename": filename["file_id"]
      }
    })

    if (blob != null) {
      if (save_file) {
        saveAs(blob, filename["filename"])
      }
      else {
        window.open(URL.createObjectURL(blob))
      }
    }

    setPageLoading(false)
  }

  const downloadAllFile = async (file_type) => {
    setPageLoading(true)

    const blob = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/attachments/download_all`,
      returnType: "blob",
      params: {
        "form_id": form_id,
        "file_type": file_type
      }
    })

    if (blob != null) {
      saveAs(blob, `${form_id}.zip`)
    }

    setPageLoading(false)
  }

  return (
    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      <div>
        <div style={{ marginBottom: "20px", display: 'flex', alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6"><b>{title}</b></Typography>
        </div>

        <div style={{ marginBottom: "10px", display: 'flex', alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body1"><b>{t("permitToWork_attachment")}</b></Typography>

          {(["admin", "pm", "so"].includes(role)) &&
            <Button variant="primary" onClick={() => downloadAllFile(step)}>
              <div style={{ display: 'flex', gap: "10px", flexDirection: "row", alignItems: "center" }}>
                <IoMdDownload />
                <div>{t("permitToWork_downloadAll")}</div>
              </div>
            </Button>
          }
        </div>

        {uploadedAttachment.filter((i) => i["file_type"] === step).map((i) => (
          <div style={{ "marginTop": "5px", "marginBottom": "5px", "borderRadius": "5px", "padding": "5px", "border": "1px solid grey", display: 'flex', gap: "10px", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
            <div>{i["filename"]}</div>  

            <div>
              <IconButtonComponent onClick={() => downloadSingleFile(i, false)}>
                <IoMdOpen size={20}/>
              </IconButtonComponent>

              {(["admin", "pm", "so"].includes(role)) &&
                <IconButtonComponent onClick={() => downloadSingleFile(i, true)}>
                  <IoMdDownload size={20}/>
                </IconButtonComponent>
              }
            </div>
          </div>
        ))}
      </div>

      <div style={{ height: "20px" }}/>

      {(step === 0 && authorizationRecord != null) && <>
        {(authorizationRecord.length > 0) && <>
          <div>
            <div style={{ marginTop: "15px", marginBottom: "15px"}}>
              <Typography variant="body1"><b>Authorization</b></Typography>
            </div>

            {authorizationRecord.map((i) =>
              <div>{i["authorization"]}: {i["name"]}</div>
            )}
          </div>
        </>}
      </>}
    </div>
  )
}

const AuditTrailDisplay = ({ auditTrailRecord, step }) => {
  auditTrailRecord = auditTrailRecord.filter((i) => i["step"] === step)

  if (auditTrailRecord.length === 0) {
    return <div></div>
  }

  auditTrailRecord = auditTrailRecord[0]

  if (step === -1) {
    return (
      <div>Created by {auditTrailRecord["username"]} at {auditTrailRecord["datetime"]}</div>
    )
  }
  if (step === -2) {
    return (
      <div>Voided by {auditTrailRecord["username"]} at {auditTrailRecord["datetime"]}</div>
    )
  }
  else if (step === 0) {
    return (
      <div>Uploaded by {auditTrailRecord["username"]} at {auditTrailRecord["datetime"]}</div>
    )
  }
  else if (step === 1) {
    return (
      <div> Uploaded by {auditTrailRecord["username"]} at {auditTrailRecord["datetime"]}</div>
    )
  }
}

const PermitToWorkDetail = () => {
  const { role, projectInfo, setPageLoading } = useOutletContext()

  const { form_id, template_id } = useParams()
  const { send } = useFetchData()
  const { t } = useTranslation()

  const [formTemplateDetail, setFormTemplateDetail] = useState({})
  const [formDetail, setFormeDetail] = useState({})
  const [uploadedAttachment, setUploadedAttachment] = useState([])

  const [authorizationItems, setAuthorizationItems] = useState([])
  const [authorizationPerson, setAuthorizationPerson] = useState([])
  const [authorizationRecord, setAuthorizationRecord] = useState()
  const [auditTrailRecord, setAuditTrailRecord] = useState()
  const [voidInfo, setVoidInfo] = useState()

  const [novadeFormDetail, setNovadeFormDetail] = useState()

  const [start, setStart] = useState(false)

  const getFormTemplateDetail = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/template_detail`,
      params: {
        "template_id": template_id
      },
      returnType: "json"
    })

    setFormTemplateDetail(body)
  }

  const getAttachment = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/attachments`,
      params: {
        "form_id": form_id
      },
      returnType: "json"
    })

    setUploadedAttachment(body)
  }

  const getAuthorizationItems = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/get_authorization_items`,
      params: {
        "template_id": template_id
      },
      returnType: "json"
    })

    setAuthorizationItems(body)
  }

  const getAuthorizationPerson = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/get_authorization_person`,
      params: {
        "template_id": template_id
      },
      returnType: "json"
    })

    setAuthorizationPerson(body.map((i) => ({
      "value": i.worker_id,
      "label": i.worker_name
    })))
  }
  
  const getAuthorizationRecord = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/get_authorization_record`,
      params: {
        "form_id": form_id
      },
      returnType: "json"
    })

    setAuthorizationRecord(body)
  }

  const getAuditTrailRecord = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/get_audit_trail_record`,
      params: {
        "form_id": form_id
      },
      returnType: "json"
    })

    setAuditTrailRecord(body)
  }

  const getVoidInfo = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/get_void_info`,
      params: {
        "form_id": form_id
      },
      returnType: "json"
    })

    setVoidInfo(body)
  }

  const getFormDetail = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/permit_to_work/form`,
      params: {
        "form_id": form_id
      },
      returnType: "json"
    })

    setFormeDetail(body)

    if (body["status"] === 0 || body["status"] === -100) {
      await Promise.all([
        getAuthorizationPerson(),
        getAuthorizationItems()
      ])
    }

    if (body["status"] === 100 || body["status"] === 101 || body["status"] === -100  || body["status"] === -101) {
      await Promise.all([
        getAttachment(),
        getAuthorizationRecord()
      ])
    }

    if (body["status"] === 102) {
      await getVoidInfo()
    }

    if (body["status"] === 0 || body["status"] === 100 || body["status"] === -100  || body["status"] === -101 || body["status"] === 100 || body["status"] === 101 || body["status"] === -100  || body["status"] === -101  || body["status"] === 102) {
      await getAuditTrailRecord()
    }
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      if (projectInfo["info"]["ptw"]["supplier"] === "novade") {
        const body = await send({
          method: "GET",
          url: `${apiUrl}/permit_to_work/form`,
          params: {
            "form_id": form_id
          },
          returnType: "json"
        })

        setNovadeFormDetail(body)
      }
      else {
        await Promise.all([
          getFormDetail(),
          getFormTemplateDetail()
        ])
      }
      
      setStart(true)
      setPageLoading(false)
    }

    init()
  }, [])

  const reload = async () => {
    await Promise.all([
      getFormDetail(),
      getFormTemplateDetail()
    ])
  }

  return (<>
    <Container fluid className="mainContainer" style={{ backgroundColor: "white", borderRadius: "15px" }}>
      <Row style={{ height: "100%" }}>
        <Col xs={12} style={{ height: "100%", paddingLeft: 0, paddingRight: 0, display: "flex", flexFlow: "column" }}>
          <BackLinkButton destination="./../../" />

          {start && <>
            {(projectInfo["info"]["ptw"]["supplier"] === "novade") ? (
              <div style={{ flexGrow: 1, overflow: "auto", margin: "10px", paddingLeft: "10px", paddingRight: "10px" }}>
                {novadeFormDetail["form_detail"] != null ? (<>
                  <div style= {{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
                    <Typography variant="h6" component="h2">
                      <u>{novadeFormDetail["form_detail"]["form_name"]}</u> 
                    </Typography>
                  </div>

                  <Typography><u>Permit Information</u></Typography>

                  <div>Contractor / Subcontractor: {novadeFormDetail["form_detail"]["contractor"] ?? "-"}</div>
                  <div>Permit Start Time: {novadeFormDetail["form_detail"]["start_date"] ?? "-"}</div>
                  <div>Permit End Time: {novadeFormDetail["form_detail"]["end_date"] ?? "-"}</div>

                  {novadeFormDetail["histories"] && <>
                    <hr />

                    <Typography><u>Workflow History</u></Typography>

                    <Stack spacing={4} style={{ marginTop: "10px" }}>
                      {novadeFormDetail["histories"].map((i) => <div>
                        <b>{i["step_name"]}</b>
                        <div>Updated at: {i["updated_at"]}</div>
                        <div>Updated by: {i["display_name"]} ({i["company"]})</div>
                      </div>)}
                    </Stack>
                  </>}

                  {novadeFormDetail["data"] && <>
                    <hr />

                    <Typography><u>Permit Detail</u></Typography>

                    <Stack spacing={4} style={{ marginTop: "10px" }}>
                      {novadeFormDetail["data"].map((i) => <div>
                        <b>{i["session_name"]}</b>

                        <Stack spacing={2} style={{ marginTop: "10px" }}>
                          {i["fields"].map((j) => <div>
                            {(() => {
                              switch (j["field_input_type"]) {
                                case "LABEL":
                                  return (<div>{j["field_name"]} </div>)
                                case "TABLE":
                                  return (<>
                                    <div>{j["field_name"]}: </div>
                                      {j["value"].map((k) => 
                                        <div>{k}</div>
                                      )}
                                  </>)
                                case "CHECKBOX":
                                  return (<>
                                    <div>{j["field_name"]}: </div>
                                    <div>{j["value"] ? t("general_yes") : t("general_no")} </div>
                                  </>)
                                default:
                                  return (<>
                                    <div>{j["field_name"]}: </div>
                                    <div>{j["value"] ? j["value"] : "-"} </div>
                                  </>)
                              }
                            })()}
                          </div>)}
                        </Stack>
                      </div>)}
                    </Stack>
                  </>}
                </>) : (
                  <div>No Information Available</div>
                )}
              </div>
            ) : (
              <div style={{ flexGrow: 1, overflow: "auto", margin: "10px", paddingLeft: "10px", paddingRight: "10px" }}>
                <div>
                  <div style= {{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
                    <Typography variant="h6" component="h2">
                      <u>{formDetail["template_name"]}</u>
                    </Typography>
                  </div>

                  <div>Workers: {formDetail["worker_name"] ?? "-"}</div>
                  <div>Company: {formDetail["company"] ?? "-"}</div>
                  <div>Work Location: {formDetail["work_location"]}</div>
                  <div>Work Description: {formDetail["work_description"]}</div>

                  {(formTemplateDetail["type"] == "switch_box") && <>
                    <div>Devices: {formDetail["lock_devices"] ?? "-"}</div>
                  </>}

                  <div>Start Time: {formDetail["permit_start_time"]}</div>
                  <div>End Time: {formDetail["permit_end_time"] ?? "-"}</div>

                  <br/>

                  {auditTrailRecord && 
                    <AuditTrailDisplay auditTrailRecord={auditTrailRecord} step={-1}/>
                  }
                </div>

                {(formTemplateDetail["e_form"] === false) && <>
                  {((formDetail["status"] === 0 || formDetail["status"] === -100) && ["admin", "pm"].includes(role)) && <>
                    <Divider sx={{ bgcolor: "secondary.light", marginTop: "20px", marginBottom: "15px" }}/>    

                    <UploadForm title={t("permitToWork_permitIssurance")} reload={reload} authorizationItems={authorizationItems} authorizationPerson={authorizationPerson} formDetail={formDetail} step={0}/>
                  
                    <Divider sx={{ bgcolor: "secondary.light", marginTop: "20px", marginBottom: "15px" }}/>    

                    <VoidForm reload={reload}/>
                  </>}

                  {(formDetail["status"] === 100 || formDetail["status"] === 101 || formDetail["status"] === -101) && <>
                    <Divider sx={{ bgcolor: "secondary.light", marginTop: "20px", marginBottom: "15px" }}/>    

                    {authorizationRecord && 
                      <ResultDisplay title={t("permitToWork_permitIssurance")} uploadedAttachment={uploadedAttachment} authorizationRecord={authorizationRecord} step={0}/>
                    }

                    {auditTrailRecord && 
                      <AuditTrailDisplay auditTrailRecord={auditTrailRecord} step={0}/>
                    }
                  </>}

                  {((formDetail["status"] === 100 || formDetail["status"] === -101) && ["admin", "pm"].includes(role)) && <>
                    <Divider sx={{ bgcolor: "secondary.light", marginTop: "20px", marginBottom: "15px" }}/>    
                  
                    <UploadForm title={t("permitToWork_permitCancellation")} reload={reload} formDetail={formDetail} step={1}/>
                  </>}

                  {(formDetail["status"] === 101) && <>
                    <Divider sx={{ bgcolor: "secondary.light", marginTop: "20px", marginBottom: "15px" }}/>    

                    {authorizationRecord && 
                      <ResultDisplay title={t("permitToWork_permitCancellation")} uploadedAttachment={uploadedAttachment} step={1}/>
                    }

                    {auditTrailRecord && 
                      <AuditTrailDisplay auditTrailRecord={auditTrailRecord} step={1}/>
                    }
                  </>}

                  {(formDetail["status"] === 102) && <>
                    <Divider sx={{ bgcolor: "secondary.light", marginTop: "20px", marginBottom: "15px" }}/>    

                    {voidInfo && 
                      <VoidDisplay voidInfo={voidInfo}/>
                    }

                    {auditTrailRecord && 
                      <AuditTrailDisplay auditTrailRecord={auditTrailRecord} step={-2}/>
                    }
                  </>}
                </>}
              </div>
            )}
          </>}
        </Col>
      </Row>
    </Container>
  </>)
}

export default PermitToWorkDetail
